import { Button, Stack } from '@form-factory/chatbot-ui';
import type { EfGroupClass } from '@form-factory/schema/contracts';
import { Trans } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import {
  BanIcon,
  PersonStandingIcon,
  UserPlusIcon,
  UsersIcon,
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';

import { EfErrorAlert } from '@/components/alerts/EfErrorAlert';
import { Loader } from '@/components/loader/Loader';
import { useClassReservations } from '@/hooks/useClassReservations';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import { aiCoreApi } from '@/services/aiCoreClient';
import { groupClassesKeys } from '@/services/queryClient';
import { useBoundStore } from '@/store/store';

export function GroupClassDetailRoute() {
  const { routeData, popRoute } = useRouter();
  const { user } = useUser();

  const { currentClubId } = useBoundStore(
    useShallow(state => ({
      currentClubId: state.groupClasses.currentClubId,
    })),
  );

  const groupClassRouteData = routeData as Partial<EfGroupClass> | null;
  const classId = groupClassRouteData?.classId?.toString();

  const {
    findReservation,
    isLoading: isReservationsLoading,
    cancelReservation,
    createReservation,
  } = useClassReservations(
    /**
     * We need to make sure to fetch reservations from the date of the class
     * so that we can check if it contains our reservation.
     */
    {
      dateFrom: groupClassRouteData?.startDate
        ? new Date(groupClassRouteData.startDate)
        : undefined,
      clubId: currentClubId,
    },
  );

  const {
    data,
    isLoading: isGroupClassLoading,
    error: groupClassError,
  } = useQuery({
    queryKey: groupClassesKeys.detail(classId ?? ''),
    queryFn: async () => {
      if (!currentClubId || !classId) {
        return;
      }

      return aiCoreApi.eFitness.groupClasses.findOne({
        clubId: currentClubId,
        classId: classId,
      });
    },
    enabled: !!currentClubId && !!classId,
  });

  const reservation = findReservation(data?.classId);
  const isLoading = isReservationsLoading || isGroupClassLoading;
  const hasStarted = data?.startDate && new Date(data?.startDate) < new Date();

  // Users can cancel up to 2 hours before the class starts
  const isTwoHoursBeforeStart = !!(
    data?.startDate &&
    new Date(data?.startDate).getTime() - Date.now() < 2 * 60 * 60 * 1000
  );

  // Users can't register for classes that start in less than 48 hours
  const timeUntilStart = data?.startDate
    ? new Date(data?.startDate).getTime() - Date.now()
    : 0;
  const is48HoursBeforeStart = timeUntilStart
    ? timeUntilStart < 48 * 60 * 60 * 1000
    : false;

  return (
    <Stack
      title={data?.classDefinitionName ?? routeData?.name}
      onBack={() => popRoute()}
    >
      <Loader visible={isLoading} />
      {!isLoading && data ? (
        <div className='space-y-4 text-chat-foreground'>
          <div className='space-y-2 rounded-md bg-chat-background-secondary p-4 shadow-sm'>
            <h3 className='text-xl font-semibold'>
              {data.classDefinitionName}
            </h3>
            <p className='text-sm text-chat-foreground opacity-75'>
              {data.duration && <Trans>{data.duration} minutes</Trans>}
              {data.roomName && <Trans> | {data.roomName}</Trans>}
            </p>
            <p className='text-chat-foreground'>
              {data.classDefinitionDescription}
            </p>
          </div>

          <div className='space-y-2 rounded-md bg-chat-background-secondary p-4 shadow-sm'>
            <h4 className='text-lg font-semibold'>
              <Trans>Participants</Trans>
            </h4>
            <div className='flex items-center gap-2'>
              <UsersIcon className='size-5' />
              {data.participantNumber}/{data.participantsLimit}
            </div>
          </div>

          <div className='space-y-2 rounded-md bg-chat-background-secondary p-4 shadow-sm'>
            <h4 className='text-lg font-semibold'>
              <Trans>Instructor</Trans>
            </h4>
            <div className='flex items-center space-x-4'>
              {data.instructorPhotoUrl ? (
                <img
                  src={data.instructorPhotoUrl}
                  alt={data.instructorFullName}
                  className='size-12 rounded-full object-cover'
                />
              ) : (
                <div className='flex size-12 items-center justify-center rounded-full bg-gray-200'>
                  <PersonStandingIcon className='size-8 text-gray-500' />
                </div>
              )}
              <div>
                <p className='font-medium'>{data.instructorFullName}</p>
                {data.instructorDescription && (
                  <p className='text-sm text-chat-foreground'>
                    {data.instructorDescription}
                  </p>
                )}
              </div>
            </div>
          </div>

          <EfErrorAlert errors={createReservation.error} />
          <EfErrorAlert errors={cancelReservation.error} />
          <EfErrorAlert errors={groupClassError} />

          {hasStarted ? (
            <div className='flex flex-col gap-2 rounded-md bg-amber-500/15 p-4 text-amber-500'>
              <Trans>The exercise has already taken place.</Trans>
            </div>
          ) : reservation ? (
            <div className='flex flex-col gap-2'>
              <Button
                disabled={cancelReservation.isPending || isTwoHoursBeforeStart}
                loading={cancelReservation.isPending}
                variant='destructive'
                size='lg'
                className='w-full'
                onClick={() =>
                  cancelReservation.mutateAsync(reservation.classReservationId)
                }
              >
                <BanIcon className='size-5' />
                <Trans>Cancel reservation</Trans>
              </Button>
            </div>
          ) : is48HoursBeforeStart && !hasStarted && user?.clubId ? (
            <div className='flex flex-col gap-2'>
              <Button
                disabled={createReservation.isPending}
                loading={createReservation.isPending}
                variant='primary'
                size='lg'
                className='w-full'
                onClick={() => createReservation.mutateAsync(data.classId)}
              >
                <UserPlusIcon className='size-5' />
                <Trans>Register</Trans>
              </Button>
            </div>
          ) : null}
        </div>
      ) : isLoading ? null : (
        <p className='text-center'>
          <Trans>Could not load class details.</Trans>
        </p>
      )}
    </Stack>
  );
}
